import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';
import { Spinner } from './Spinner';
import type { Placement } from './floating';
import { Tooltip } from './Tooltip';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner in place of the icon and disables the button. */
  loading?: boolean;
  /** Leading icon (e.g. `<Plus />` from lucide-react). */
  icon?: ReactNode;
  /** Trailing icon. */
  iconEnd?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

/** Text button. Defaults to `type="button"` (pass `type="submit"` in forms). */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  iconEnd,
  className,
  children,
  disabled,
  type = 'button',
  ref,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(styles.button, styles[variant], styles[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" label={null} /> : icon}
      {children !== undefined && children !== null && <span className={styles.label}>{children}</span>}
      {iconEnd}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon' | 'iconEnd'> {
  /** Accessible name, also shown as tooltip. */
  label: string;
  icon: ReactNode;
  /** Hotkey combo shown in the tooltip (e.g. `"mod+z"`). */
  shortcut?: string;
  /** Toggle buttons (tools): sets `aria-pressed` and the pressed style. */
  pressed?: boolean;
  /** Hide the tooltip (the label stays the accessible name). */
  noTooltip?: boolean;
  /** Where the tooltip opens (default above; vertical toolbars use `"right"`). */
  tooltipPlacement?: Placement;
}

/** Square icon-only button with a tooltip. */
export function IconButton({
  label,
  icon,
  shortcut,
  pressed,
  noTooltip = false,
  tooltipPlacement,
  variant = 'ghost',
  className,
  ...rest
}: IconButtonProps) {
  const button = (
    <Button
      {...rest}
      variant={variant}
      aria-label={label}
      aria-pressed={pressed}
      className={clsx(styles.iconOnly, pressed && styles.pressed, className)}
      icon={icon}
    />
  );
  if (noTooltip) return button;
  return (
    <Tooltip content={label} shortcut={shortcut} placement={tooltipPlacement}>
      {button}
    </Tooltip>
  );
}

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
}

/** In-app navigation styled as a button (never nest a `<Button>` inside a `<Link>`). */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  icon,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link {...rest} className={clsx(styles.button, styles.link, styles[variant], styles[size], className)}>
      {icon}
      <span className={styles.label}>{children}</span>
    </Link>
  );
}
